import { FC, useContext, useState, useEffect, useCallback, useRef } from 'react';
import {
  ShoppingBag, RefreshCw, Plus, AlertTriangle, Package, DollarSign,
  CheckCircle, Clock, XCircle, TrendingUp, MapPin, ArrowUpRight, ArrowDownRight,
  Database, ChevronDown, ChevronUp, Wallet, LayoutDashboard,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import ContextStore from '../hooks/ContextStore';
import { dispatch } from '../dispatch/dispatch';
import { actions } from '../dispatch';
import MarjanFinance from '../components/marjan/MarjanFinance';
import './Marjan.page.css';

type Tab = 'overview' | 'profit';

// ─── Types ────────────────────────────────────────────────────────────────────
interface SyncState {
  status: 'idle' | 'running' | 'error';
  phase: string | null;
  progress: string | null;
  lastRunAt: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
  backfilledFrom: string | null;
  ordersCount: number;
}

interface MarjanStore {
  _id: string;
  name: string;
  sellerId: string | null;
  status: boolean;
  lastSyncAt: string | null;
  marjane?: { email?: string; fullName?: string | null; lastAuthError?: any };
  sync?: SyncState;
}

interface OrderLine {
  productId: string | null;
  sku: string | null;
  gtin: string | null;
  title: string | null;
  status: string | null;
  quantity: number;
  unitPrice: number | null;
}

interface MarjanOrder {
  storeName?: string | null;
  orderReference: string;
  status: string;
  statusRaw: string | null;
  orderedAt: string | null;
  itemCount: number;
  total: number | null;
  customerName: string | null;
  city: string | null;
  lines: OrderLine[];
}

interface DailyPoint {
  date: string;
  orders: number;
  revenue: number;
  delivered: number;
  cancelled: number;
}

interface TopProduct {
  productId: string | null;
  image?: string | null;
  sku: string | null;
  title: string | null;
  quantity: number;
  delivered: number;
  cancelled: number;
  revenue: number;
  orders: number;
}

interface Overview {
  store: { _id: string; name: string; sellerId: string | null };
  days: number;
  kpis: {
    totalOrders: number;
    revenue: number;
    deliveredRevenue: number;
    itemsSold: number;
    deliveredCount: number;
    cancelledCount: number;
    inProgressCount: number;
    cancelRate: number;
    avgOrderValue: number;
    trends: {
      totalOrders: number | null;
      revenue: number | null;
      deliveredCount: number | null;
      cancelRate: number | null;
    };
  };
  statusBreakdown: Record<string, number>;
  daily: DailyPoint[];
  topProducts: TopProduct[];
  topCities: { city: string; count: number }[];
  recentOrders: MarjanOrder[];
  sync: SyncState | null;
  dataStartsAt: string;
  syncedAt: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtMAD = (v: number | null | undefined) =>
  v == null ? '—' : `${Math.round(v).toLocaleString('en-US')} MAD`;

const STATUS_COLORS: Record<string, string> = {
  Delivered: '#1D9E75',
  Shipped: '#378ADD',
  Prepared: '#378ADD',
  InPreparation: '#EF9F27',
  Accepted: '#EF9F27',
  Cancelled: '#E24B4A',
  Refused: '#E24B4A',
  Returned: '#E24B4A',
};

const STATUS_LABEL: Record<string, string> = {
  Delivered: 'Delivered',
  Shipped: 'Shipped',
  Prepared: 'Ready to ship',
  InPreparation: 'Preparing',
  Accepted: 'Accepted',
  Cancelled: 'Cancelled',
  Refused: 'Refused',
  Returned: 'Returned',
};

const statusBadgeClass = (status: string): string => {
  if (status === 'Delivered') return 'mj__badge--green';
  if (['Shipped', 'Prepared'].includes(status)) return 'mj__badge--blue';
  if (['Cancelled', 'Refused', 'Returned'].includes(status)) return 'mj__badge--red';
  if (['Accepted', 'InPreparation'].includes(status)) return 'mj__badge--orange';
  return 'mj__badge--gray';
};

const dayLabel = (iso: string) => {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
};

const DAY_OPTIONS = [7, 14, 30, 60, 90, 180];

const Trend: FC<{ value: number | null | undefined; invert?: boolean; suffix?: string }> = ({ value, invert, suffix = '%' }) => {
  if (value == null) return <span className="mj__trend mj__trend--flat">— vs prev</span>;
  const good = invert ? value <= 0 : value >= 0;
  const Icon = value >= 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`mj__trend ${good ? 'mj__trend--good' : 'mj__trend--bad'}`}>
      <Icon size={11} /> {Math.abs(value).toFixed(value % 1 === 0 ? 0 : 1)}{suffix} vs prev
    </span>
  );
};

// ─── Chart tooltips ───────────────────────────────────────────────────────────
const OrdersTooltip = ({ active, payload, label }: any) =>
  active && payload?.length ? (
    <div className="mj__tooltip">
      <div style={{ color: '#9CA3AF', marginBottom: 2 }}>{dayLabel(label)}</div>
      <div><strong>{payload[0].payload.orders}</strong> orders</div>
      <div style={{ color: '#1D9E75' }}>{payload[0].payload.delivered} delivered</div>
      {payload[0].payload.cancelled > 0 && (
        <div style={{ color: '#E24B4A' }}>{payload[0].payload.cancelled} cancelled</div>
      )}
    </div>
  ) : null;

const RevenueTooltip = ({ active, payload, label }: any) =>
  active && payload?.length ? (
    <div className="mj__tooltip">
      <div style={{ color: '#9CA3AF', marginBottom: 2 }}>{dayLabel(label)}</div>
      <div><strong>{fmtMAD(payload[0].payload.revenue)}</strong></div>
    </div>
  ) : null;

// ─── Page ─────────────────────────────────────────────────────────────────────
const MarjanPage: FC = () => {
  const { contextStore } = useContext(ContextStore) as any;
  const token = contextStore?.token;
  const user = contextStore?.user;
  // Payouts & Profit is its own permission (User Management → "Marjan Payouts & Profit")
  const canProfit: boolean = !!user && (user.type === 'admin' || (user.permissions || []).includes('all') || (user.permissions || []).includes('marjan-profit'));

  const [stores, setStores] = useState<MarjanStore[]>([]);
  const [storesLoaded, setStoresLoaded] = useState(false);
  // 'all' = every enabled Marjan store aggregated (default, like the Jumia dashboard)
  const [selectedId, setSelectedId] = useState<string>('all');
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState<Tab>(() => (window.location.hash === '#profit' ? 'profit' : 'overview'));
  useEffect(() => { if (!canProfit && tab === 'profit') setTab('overview'); }, [canProfit, tab]);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncState, setSyncState] = useState<SyncState | null>(null);
  const [expandedOrder, setExpandedOrder] = useState<string | null>(null);
  const pollRef = useRef<any>(null);

  const [showForm, setShowForm] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', password: '', sellerId: '', clientId: '', clientSecret: '', salesChannelId: '',
  });

  const loadStores = useCallback(async () => {
    const resp = await dispatch(actions.getMarjanStores, {}, token);
    if (Array.isArray(resp)) {
      setStores(resp);
      if (resp.length && !selectedId) setSelectedId('all');
    } else if (resp?.error) {
      setError(resp.error.message);
    }
    setStoresLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadOverview = useCallback(async (storeId: string, rangeDays: number, silent = false) => {
    if (!storeId) return;
    if (!silent) setLoading(true);
    setError(null);
    const resp = await dispatch(actions.getMarjanOverview, {}, token, {
      id: storeId, days: rangeDays,
    });
    if (resp?.error) {
      setError(resp.error.message);
      if (!silent) setOverview(null);
    } else if (resp?.kpis) {
      setOverview(resp);
      setSyncState(resp.sync ?? null);
    }
    if (!silent) setLoading(false);
  }, [token]);

  useEffect(() => { loadStores(); }, [loadStores]);
  useEffect(() => {
    if (selectedId) loadOverview(selectedId, days);
  }, [selectedId, days, loadOverview]);

  // Poll sync status while a sync is running, and refresh the overview as data lands
  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (!selectedId || syncState?.status !== 'running') return;
    pollRef.current = setInterval(async () => {
      const resp = await dispatch(actions.getMarjanSyncStatus, {}, token, { id: selectedId });
      if (resp?.sync) {
        setSyncState(resp.sync);
        loadOverview(selectedId, days, true);
      }
    }, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selectedId, syncState?.status, token, days, loadOverview]);

  const triggerSync = async (mode: 'incremental' | 'backfill' = 'incremental') => {
    if (!selectedId) return;
    const resp = await dispatch(actions.syncMarjanStore, { mode }, token, { id: selectedId });
    if (resp?.error) { setError(resp.error.message); return; }
    setSyncState(prev => ({ ...(prev || {} as any), status: 'running', phase: mode, progress: 'starting…' }));
  };

  const handleCreate = async () => {
    if (!form.name || !form.email || !form.password) {
      setError('Store name, Seller Zone email and password are required');
      return;
    }
    setSaving(true);
    setError(null);
    const resp = await dispatch(actions.createMarjanStore, {
      name: form.name,
      email: form.email,
      password: form.password,
      sellerId: form.sellerId || null,
      clientId: form.clientId || null,
      clientSecret: form.clientSecret || null,
      salesChannelId: form.salesChannelId || null,
    }, token);
    setSaving(false);
    if (resp?.error) {
      setError(resp.error.message);
      return;
    }
    setForm({ name: '', email: '', password: '', sellerId: '', clientId: '', clientSecret: '', salesChannelId: '' });
    setShowForm(false);
    setStores(prev => [...prev, resp]);
    setSelectedId(resp._id);
    setSyncState({ status: 'running', phase: 'backfill', progress: 'starting…' } as SyncState);
  };

  const handleDelete = async (storeId: string) => {
    if (!window.confirm('Remove this Marjan store connection and its synced orders from the dashboard? (Nothing is changed on Marjane Mall.)')) return;
    const resp = await dispatch(actions.deleteMarjanStore, {}, token, { id: storeId });
    if (resp?.error) { setError(resp.error.message); return; }
    const remaining = stores.filter(s => s._id !== storeId);
    setStores(remaining);
    setSelectedId(remaining.length ? 'all' : '');
    if (!remaining.length) setOverview(null);
  };

  const kpis = overview?.kpis;
  const hasOrders = (kpis?.totalOrders ?? 0) > 0;
  const isAll = selectedId === 'all';
  const selectedStore = stores.find(s => s._id === selectedId);
  const isSyncing = syncState?.status === 'running';

  // ── Connect form (empty state + "add store") ────────────────────────────────
  const connectForm = (
    <div className="mj__connect-wrap">
      <div className="mj__connect-card">
        <div className="mj__connect-title">
          <ShoppingBag size={17} color="#E24B4A" />
          Connect a Marjan store
        </div>
        <div className="mj__connect-sub">
          Enter the <strong>Marjane Mall Seller Zone</strong> login of the store (the same
          email &amp; password you use on seller.marjanemall.ma). Orders are pulled from
          Marjane's sellers API and kept in sync automatically every 2 hours.
        </div>
        <div className="mj__field">
          <label className="mj__label">Store name</label>
          <input className="mj__input" placeholder="e.g. Happy Choice"
            value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="mj__field">
          <label className="mj__label">Seller Zone email</label>
          <input className="mj__input" type="email" placeholder="seller@email.com" autoComplete="off"
            value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
        </div>
        <div className="mj__field">
          <label className="mj__label">Seller Zone password</label>
          <input className="mj__input" type="password" placeholder="••••••••" autoComplete="new-password"
            value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
        </div>

        <button type="button" className="mj__link-btn" style={{ alignSelf: 'flex-start', padding: '2px 0' }}
          onClick={() => setShowAdvanced(v => !v)}>
          {showAdvanced ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Optional: Octopia API credentials
        </button>
        {showAdvanced && (
          <>
            <div className="mj__connect-hint" style={{ marginTop: 0 }}>
              Only needed for the legacy Octopia offers/stock read. Orders do not need this.
            </div>
            <div className="mj__field">
              <label className="mj__label">Seller ID</label>
              <input className="mj__input" placeholder="auto-detected from login if empty"
                value={form.sellerId} onChange={e => setForm({ ...form, sellerId: e.target.value })} />
            </div>
            <div className="mj__field">
              <label className="mj__label">Octopia Client ID</label>
              <input className="mj__input" value={form.clientId}
                onChange={e => setForm({ ...form, clientId: e.target.value })} />
            </div>
            <div className="mj__field">
              <label className="mj__label">Octopia Client Secret</label>
              <input className="mj__input" type="password" value={form.clientSecret}
                onChange={e => setForm({ ...form, clientSecret: e.target.value })} />
            </div>
            <div className="mj__field">
              <label className="mj__label">Sales Channel ID</label>
              <input className="mj__input" placeholder="MARJMA" value={form.salesChannelId}
                onChange={e => setForm({ ...form, salesChannelId: e.target.value })} />
            </div>
          </>
        )}

        <button className="mj__connect-btn" onClick={handleCreate} disabled={saving}>
          {saving ? 'Testing login…' : 'Test & Connect'}
        </button>
        <div className="mj__connect-hint">
          The login is verified against Marjane's API before saving. After connecting, the
          full order history (since Jan 2026) is imported in the background — this takes a
          few minutes the first time.
        </div>
      </div>
    </div>
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="mj">
      <div className="mj__topbar">
        <div className="mj__title-block">
          <div className="mj__title-icon"><ShoppingBag size={15} color="#fff" /></div>
          <div>
            <div className="mj__title">Marjan Stores</div>
            <div className="mj__subtitle">Marjane Mall · sellers API</div>
          </div>
        </div>

        {stores.length > 0 && (
          <>
            <div className="mj__tabs">
              <button className={`mj__tab ${tab === 'overview' ? 'mj__tab--active' : ''}`}
                onClick={() => { setTab('overview'); window.location.hash = ''; }}>
                <LayoutDashboard size={13} /> Orders
              </button>
              {canProfit && (
                <button className={`mj__tab ${tab === 'profit' ? 'mj__tab--active' : ''}`}
                  onClick={() => { setTab('profit'); window.location.hash = 'profit'; }}>
                  <Wallet size={13} /> Payouts &amp; Profit
                </button>
              )}
            </div>
            <select className="mj__select" value={selectedId}
              onChange={e => setSelectedId(e.target.value)}>
              <option value="all">All Stores ({stores.length})</option>
              {stores.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
            </select>
            <select className="mj__select" value={days}
              onChange={e => setDays(Number(e.target.value))}>
              {DAY_OPTIONS.map(d => <option key={d} value={d}>Last {d} days</option>)}
            </select>
            {tab === 'overview' && <button className="mj__refresh-btn" disabled={loading || isSyncing}
              onClick={() => triggerSync('incremental')}
              title="Pull the latest orders & status changes from Marjane now">
              <RefreshCw size={13} className={loading || isSyncing ? 'mj__refresh-spin' : ''} />
              {isSyncing ? 'Syncing…' : 'Sync now'}
            </button>}
            <button className="mj__add-btn" onClick={() => setShowForm(v => !v)}>
              <Plus size={13} /> Add Store
            </button>
            {overview?.syncedAt && !isSyncing && (
              <span className="mj__last-updated">
                Last sync {new Date(overview.syncedAt).toLocaleString()}
              </span>
            )}
          </>
        )}
      </div>

      <div className="mj__body">
        {error && (
          <div className="mj__error-banner">
            <AlertTriangle size={14} /> {error}
          </div>
        )}

        {/* Sync status banner */}
        {stores.length > 0 && !showForm && syncState && (syncState.status === 'running' || syncState.status === 'error') && (
          <div className={`mj__sync-banner ${syncState.status === 'error' ? 'mj__sync-banner--error' : ''}`}>
            {syncState.status === 'running' ? (
              <>
                <RefreshCw size={13} className="mj__refresh-spin" />
                <span>
                  <strong>{syncState.phase === 'backfill' ? 'Importing order history' : 'Syncing latest orders'}</strong>
                  {syncState.progress ? ` — ${syncState.progress}` : '…'}
                </span>
              </>
            ) : (
              <>
                <AlertTriangle size={13} />
                <span><strong>Last sync failed:</strong> {syncState.lastError}</span>
                <button className="mj__link-btn" onClick={() => triggerSync('incremental')}>Retry</button>
              </>
            )}
          </div>
        )}

        {/* Empty state / add-store form */}
        {storesLoaded && (stores.length === 0 || showForm) && connectForm}

        {stores.length > 0 && !showForm && tab === 'profit' && canProfit && selectedId && (
          <MarjanFinance storeId={selectedId} token={token} days={days} onError={setError} />
        )}

        {stores.length > 0 && !showForm && tab === 'overview' && (
          <>
            {/* ── KPI cards ─────────────────────────────────────────────── */}
            <div className="mj__kpi-grid">
              <div className="mj__kpi-card">
                <div className="mj__kpi-label"><Package size={12} /> Total Orders</div>
                <div className="mj__kpi-value">{kpis ? kpis.totalOrders : '—'}</div>
                <div className="mj__kpi-sub">
                  {kpis ? `${kpis.itemsSold} items` : `last ${days} days`}
                  {kpis && <Trend value={kpis.trends.totalOrders} />}
                </div>
              </div>
              <div className="mj__kpi-card">
                <div className="mj__kpi-label"><DollarSign size={12} /> Revenue</div>
                <div className="mj__kpi-value">{kpis ? fmtMAD(kpis.revenue) : '—'}</div>
                <div className="mj__kpi-sub">
                  {kpis ? `${fmtMAD(kpis.deliveredRevenue)} delivered` : ''}
                  {kpis && <Trend value={kpis.trends.revenue} />}
                </div>
              </div>
              <div className="mj__kpi-card">
                <div className="mj__kpi-label"><CheckCircle size={12} color="#1D9E75" /> Delivered</div>
                <div className="mj__kpi-value">{kpis ? kpis.deliveredCount : '—'}</div>
                <div className="mj__kpi-sub">
                  {kpis && kpis.totalOrders ? `${Math.round((kpis.deliveredCount / kpis.totalOrders) * 100)}% of orders` : 'completed orders'}
                  {kpis && <Trend value={kpis.trends.deliveredCount} />}
                </div>
              </div>
              <div className="mj__kpi-card">
                <div className="mj__kpi-label"><Clock size={12} color="#EF9F27" /> In Progress</div>
                <div className="mj__kpi-value">{kpis ? kpis.inProgressCount : '—'}</div>
                <div className="mj__kpi-sub">accepted / preparing / shipped</div>
              </div>
              <div className="mj__kpi-card">
                <div className="mj__kpi-label"><XCircle size={12} color="#E24B4A" /> Cancelled</div>
                <div className="mj__kpi-value">{kpis ? kpis.cancelledCount : '—'}</div>
                <div className="mj__kpi-sub">
                  {kpis ? `${kpis.cancelRate}% of orders` : ''}
                  {kpis && <Trend value={kpis.trends.cancelRate} invert suffix=" pts" />}
                </div>
              </div>
              <div className="mj__kpi-card">
                <div className="mj__kpi-label"><TrendingUp size={12} /> Avg Order</div>
                <div className="mj__kpi-value">{kpis ? fmtMAD(kpis.avgOrderValue) : '—'}</div>
                <div className="mj__kpi-sub">per valid order</div>
              </div>
            </div>

            {/* ── Status breakdown ──────────────────────────────────────── */}
            {overview && (
              <div className="mj__status-row">
                {Object.entries(overview.statusBreakdown)
                  .sort((a, b) => b[1] - a[1])
                  .map(([status, count]) => (
                    <span key={status} className="mj__status-chip">
                      <span className="mj__status-dot"
                        style={{ background: STATUS_COLORS[status] || '#9CA3AF' }} />
                      {STATUS_LABEL[status] || status} · {count}
                    </span>
                  ))}
                {syncState && syncState.ordersCount > 0 && (
                  <span className="mj__status-chip" title="Total orders stored for this store">
                    <Database size={11} color="#9CA3AF" />
                    {syncState.ordersCount.toLocaleString()} orders in history
                  </span>
                )}
              </div>
            )}

            {/* ── Charts ────────────────────────────────────────────────── */}
            {overview && hasOrders && (
              <div className="mj__charts-row">
                <div className="mj__panel">
                  <div className="mj__panel-header">
                    <Package size={13} color="#378ADD" />
                    <span className="mj__panel-title">Orders per day</span>
                    <span className="mj__panel-count">{kpis?.totalOrders} total</span>
                  </div>
                  <div className="mj__panel-body" style={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={overview.daily} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#9CA3AF' }}
                          tickFormatter={dayLabel} axisLine={false} tickLine={false}
                          interval="preserveStartEnd" minTickGap={28} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 9, fill: '#9CA3AF' }}
                          axisLine={false} tickLine={false} />
                        <Tooltip content={<OrdersTooltip />} cursor={{ fill: 'rgba(55,138,221,0.06)' }} />
                        <Bar dataKey="orders" fill="#378ADD" radius={[4, 4, 0, 0]} maxBarSize={22} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="mj__panel">
                  <div className="mj__panel-header">
                    <DollarSign size={13} color="#1D9E75" />
                    <span className="mj__panel-title">Revenue per day</span>
                    <span className="mj__panel-count">{fmtMAD(kpis?.revenue)}</span>
                  </div>
                  <div className="mj__panel-body" style={{ height: 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={overview.daily} margin={{ top: 6, right: 6, left: -12, bottom: 0 }}>
                        <defs>
                          <linearGradient id="mjRev" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#1D9E75" stopOpacity={0.25} />
                            <stop offset="100%" stopColor="#1D9E75" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#9CA3AF' }}
                          tickFormatter={dayLabel} axisLine={false} tickLine={false}
                          interval="preserveStartEnd" minTickGap={28} />
                        <YAxis tick={{ fontSize: 9, fill: '#9CA3AF' }}
                          tickFormatter={(v: number) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`)}
                          axisLine={false} tickLine={false} />
                        <Tooltip content={<RevenueTooltip />} />
                        <Area type="monotone" dataKey="revenue" stroke="#1D9E75"
                          strokeWidth={2} fill="url(#mjRev)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            )}

            {overview && !hasOrders && !loading && (
              <div className="mj__panel">
                <div className="mj__empty-note">
                  {isSyncing
                    ? 'Orders are being imported — this view fills in automatically as data lands.'
                    : `No orders in the last ${days} days for this store. Try a wider range, or click "Sync now" to pull the latest data from Marjane.`}
                </div>
              </div>
            )}

            {/* ── Top products + Top cities ─────────────────────────────── */}
            {overview && hasOrders && (
              <div className="mj__tables-row">
                <div className="mj__panel">
                  <div className="mj__panel-header">
                    <TrendingUp size={13} color="#1D9E75" />
                    <span className="mj__panel-title">Top Products</span>
                    <span className="mj__panel-count">by revenue</span>
                  </div>
                  <div className="mj__table-scroll">
                    {overview.topProducts.length === 0 ? (
                      <div className="mj__empty-note">No product lines in this range</div>
                    ) : (
                      <table className="mj__table">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th className="num">Sold</th>
                            <th className="num">Delivered</th>
                            <th className="num">Cancelled</th>
                            <th className="num">Revenue</th>
                          </tr>
                        </thead>
                        <tbody>
                          {overview.topProducts.map((p, i) => (
                            <tr key={i}>
                              <td>
                                <div className="mjf__prod">
                                  {p.image ? <img className="mjf__prod-img" src={p.image} alt="" /> : <div className="mjf__prod-img mjf__prod-img--empty"><Package size={13} /></div>}
                                  <div style={{ minWidth: 0 }}>
                                    <div className="mj__cell-title" title={p.title || ''}>{p.title || p.sku || p.productId || '—'}</div>
                                    <div className="mj__cell-sku">
                                      {p.sku || p.productId}{p.sku && p.productId ? ` · ${p.productId}` : ''}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="num">{p.quantity}</td>
                              <td className="num" style={{ color: '#1D9E75' }}>{p.delivered}</td>
                              <td className="num" style={{ color: p.cancelled ? '#E24B4A' : undefined }}>{p.cancelled || '—'}</td>
                              <td className="num">{fmtMAD(p.revenue)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>

                <div className="mj__panel">
                  <div className="mj__panel-header">
                    <MapPin size={13} color="#E24B4A" />
                    <span className="mj__panel-title">Top Cities</span>
                    <span className="mj__panel-count">valid orders</span>
                  </div>
                  <div className="mj__table-scroll">
                    {overview.topCities.length === 0 ? (
                      <div className="mj__empty-note">No city data</div>
                    ) : (
                      <table className="mj__table">
                        <tbody>
                          {overview.topCities.map((c) => {
                            const max = overview.topCities[0].count || 1;
                            return (
                              <tr key={c.city}>
                                <td style={{ width: '40%' }}>{c.city}</td>
                                <td>
                                  <div className="mj__bar-track">
                                    <div className="mj__bar-fill" style={{ width: `${(c.count / max) * 100}%` }} />
                                  </div>
                                </td>
                                <td className="num" style={{ width: 48 }}>{c.count}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── Recent orders ─────────────────────────────────────────── */}
            {overview && hasOrders && (
              <div className="mj__panel">
                <div className="mj__panel-header">
                  <Package size={13} color="#378ADD" />
                  <span className="mj__panel-title">Recent Orders</span>
                  <span className="mj__panel-count">last {overview.recentOrders.length}</span>
                </div>
                <div className="mj__table-scroll">
                  <table className="mj__table">
                    <thead>
                      <tr>
                        {isAll && <th>Store</th>}
                        <th>Reference</th>
                        <th>Date</th>
                        <th>Customer</th>
                        <th>City</th>
                        <th className="num">Items</th>
                        <th className="num">Total</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overview.recentOrders.map((o) => {
                        const open = expandedOrder === o.orderReference;
                        return (
                          <>
                            <tr key={o.orderReference} className="mj__row-click"
                              onClick={() => setExpandedOrder(open ? null : o.orderReference)}>
                              {isAll && <td><span className="mj__badge mj__badge--gray">{o.storeName || '—'}</span></td>}
                              <td>{o.orderReference}</td>
                              <td>{o.orderedAt ? new Date(o.orderedAt).toLocaleDateString() : '—'}</td>
                              <td>{o.customerName || '—'}</td>
                              <td>{o.city || '—'}</td>
                              <td className="num">{o.itemCount}</td>
                              <td className="num">{fmtMAD(o.total)}</td>
                              <td>
                                <span className={`mj__badge ${statusBadgeClass(o.status)}`} title={o.statusRaw || ''}>
                                  {STATUS_LABEL[o.status] || o.status}
                                </span>
                              </td>
                            </tr>
                            {open && (
                              <tr key={`${o.orderReference}-lines`} className="mj__row-detail">
                                <td colSpan={isAll ? 8 : 7}>
                                  {o.lines.map((l, i) => (
                                    <div key={i} className="mj__line">
                                      <span className="mj__line-title">{l.title || '—'}</span>
                                      <span className="mj__cell-sku">{l.sku || l.productId}</span>
                                      <span className="mj__line-meta">×{l.quantity} · {fmtMAD(l.unitPrice)}</span>
                                      <span className={`mj__badge ${statusBadgeClass(l.status || '')}`}>{STATUS_LABEL[l.status || ''] || l.status}</span>
                                    </div>
                                  ))}
                                </td>
                              </tr>
                            )}
                          </>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── Store management footer ───────────────────────────────── */}
            {selectedId && !isAll && (
              <div className="mj__store-actions">
                <span className="mj__last-updated">
                  {selectedStore?.marjane?.fullName ? `${selectedStore.marjane.fullName} · ` : ''}
                  {selectedStore?.marjane?.email}
                  {selectedStore?.sellerId ? ` · Seller ID ${selectedStore.sellerId}` : ''}
                  {overview?.dataStartsAt ? ` · history from ${new Date(overview.dataStartsAt).toLocaleDateString()}` : ''}
                </span>
                <button className="mj__link-btn" disabled={isSyncing}
                  onClick={() => triggerSync('backfill')}
                  title="Re-import the entire order history">
                  Re-import history
                </button>
                <button className="mj__link-btn mj__link-btn--danger"
                  onClick={() => handleDelete(selectedId)}>
                  Remove store
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MarjanPage;
